import { useNavigate, useParams } from "react-router-dom";
import "./start-room.page.scss"
import { useEffect, useRef, useState } from "react";
import { trackInviteEmailLinkOpen, verifyRoom } from "../services/interview-api.services";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { Checkbox, CircularProgress, FormControl, FormControlLabel } from "@mui/material";
import { useLanguage } from "../providers/language.provider";
import ErrorModal from "../modals/error-message.modal";
import LOGO_SO from "../assets/images/logo-so-black.png"

const StartRoomPage = () => {
    const { translations } = useLanguage();
    const { id, candidate_id } = useParams<{ id: string; candidate_id: string }>();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [isError, setIsError] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const [isSubmit, setIsSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [isHardware, setIsHardware] = useState(false);
    const [isPrivacy, setIsPrivacy] = useState(false);
    const inviteLinkTrackedRef = useRef(false);

    useEffect(() => {
        if (!id || !candidate_id || inviteLinkTrackedRef.current) return;
        inviteLinkTrackedRef.current = true;
        trackInviteEmailLinkOpen({ job_question_id: id, candidate_id });
    }, [id, candidate_id]);

    const onEmailChange = (value: string) => {
        setEmailError(false);
        setEmail(value);
    };

    const handleBack = () => {
        navigate("/" + id + "/" + candidate_id + "/");
    };

    const submit = () => {
        setIsSubmit(true);
        if (id && candidate_id && email && isHardware && isPrivacy) {
            setIsLoading(true);
            verifyRoom(id, candidate_id, email).then((res) => {
                if (res.is_success) {
                    navigate("/" + id + "/" + candidate_id + "/" + email + "/interview-room");
                }
            }).catch((error) => {
                setIsLoading(false);
                const errCode = error?.response?.data?.error;
                if (errCode === "This candidate already interview") {
                    setErrMsg("ผู้สมัครเคยสัมภาษณ์ไปแล้ว");
                } else if (errCode === "Email does not match for the provided candidate_id") {
                    setErrMsg("อีเมลผู้สมัครไม่ตรงในระบบ");
                } else if (errCode === "candidate_id not found in candidates list") {
                    setErrMsg("ไม่พบรายชื่อผู้สมัครในระบบ");
                }else if(errCode === "Room not found with the provided information") {
                    setErrMsg("ไม่พบห้องสัมภาษณ์");
                }else if(errCode === "Candidate not found with the provided information") {
                    setErrMsg("ไม่พบรายชื่อผู้สมัครในระบบ");
                } else if (errCode === "Interview period has ended") {
                    setErrMsg("ลิงก์สัมภาษณ์หมดอายุแล้ว");
                } else if (errCode === "Interview period has not started yet") {
                    setErrMsg("ยังไม่ถึงช่วงเวลาที่เปิดสัมภาษณ์");
                }else {
                    setErrMsg("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
                }
                setIsError(true);
            });
        } else {
            setIsError(true);
            setErrMsg("กรุณากรอกข้อมูลให้ครบถ้วน");
        }
    };

    return (
        <>
            <div className="sr-page">
                {/* Header */}
                <div className="sr-header">
                    <button className="sr-back-btn" onClick={handleBack} aria-label="back">
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <img src={LOGO_SO} className="sr-logo" alt="logo" />
                    <div className="sr-header-spacer" />
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="sr-loading">
                        <CircularProgress className="sr-progress" />
                        <span className="sr-loading-text">{translations.verify.loading}</span>
                    </div>
                ) : (
                    <div className="sr-form-wrap">
                        <div className="sr-icon-wrap">
                            <FontAwesomeIcon icon={faEnvelope} className="sr-icon" />
                        </div>
                        <span className="sr-title">{translations.verify.title}</span>

                        <input
                            type="email"
                            placeholder={translations.verify.email}
                            className={`sr-input${isSubmit && emailError ? ' error' : ''}`}
                            onChange={(e) => onEmailChange(e.target.value)}
                            value={email}
                        />

                        <FormControl fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isHardware}
                                        onChange={(e) => setIsHardware(e.target.checked)}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fill: "white",
                                                borderRadius: "6px",
                                                border: `2px solid ${isHardware ? "#9333ea" : isSubmit && !isHardware ? "#ef4123" : "#9333ea"}`,
                                                backgroundColor: isHardware ? "#9333ea" : "#f4f0ff",
                                            },
                                        }}
                                    />
                                }
                                label={<span className="sr-check-label">{translations.verify.check_1}</span>}
                            />
                        </FormControl>

                        <FormControl fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isPrivacy}
                                        onChange={(e) => setIsPrivacy(e.target.checked)}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fill: "white",
                                                borderRadius: "6px",
                                                border: `2px solid ${isPrivacy ? "#9333ea" : isSubmit && !isPrivacy ? "#ef4123" : "#9333ea"}`,
                                                backgroundColor: isPrivacy ? "#9333ea" : "#f4f0ff",
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <span className="sr-check-label">
                                        {translations.verify.check_2}{' '}
                                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="sr-link">
                                            {translations.verify.check_2_1}
                                        </a>{' '}
                                        {translations.verify.check_2_and}{' '}
                                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="sr-link">
                                            {translations.verify.check_2_2}
                                        </a>
                                    </span>
                                }
                            />
                        </FormControl>

                        <Button className="sr-submit-btn" onClick={submit}>
                            {translations.next_button}
                            <FontAwesomeIcon icon={faArrowRight} className="sr-submit-icon" />
                        </Button>
                    </div>
                )}
            </div>

            {isError && (
                <ErrorModal message={errMsg} onClose={() => setIsError(false)} isOpen={isError} />
            )}
        </>
    );
};

export default StartRoomPage;
